/**
 * The frame every signed-in page sits in: a header bar across the top — the
 * brand, the site-wide search, Settings and the account menu (D-086) — a side
 * menu on the left and the page. The rail collapses to a strip of icons on one click (D-084); the
 * choice is the person's preference (`nav.collapsed`) with a local copy for the first paint. On a
 * narrow screen (D-215) the rail is a drawer instead, opened by the header's menu button and closed
 * by a pick, the scrim or Esc; it never folds to icons there, whatever the preference says.
 */

import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { fetchHealth, fetchNavCollapsedPreference, putNavCollapsedPreference } from '../lib/api.js';
import { appVersion } from '../lib/config.js';
import { railFolded, useNarrow } from '../lib/narrow.js';
import { SETTINGS_LINK, canSee, foldSubItems, navFor } from '../lib/nav.js';
import { NAV_COLLAPSED_STORAGE_KEY, browserStorage, readStoredFlag, resolveFlag, storeFlag } from '../lib/storedFlag.js';
import { keepActiveTabsInView } from '../lib/tabsInView.js';
import { useRoles, useSessionContext } from '../lib/session.js';
import { useLoad } from '../lib/useLoad.js';
import { AccountMenu } from './AccountMenu.js';
import { HomeOpening } from './HomeOpening.js';
import { Icon } from './Icon.js';
import { MeetingEntranceProvider } from './MeetingEntrance.js';
import { PageBoundary } from './PageErrorBoundary.js';
import { SearchBox } from './SearchBox.js';

/**
 * The version line at the foot of the account menu: which web build this is and
 * which API release answered. One string when they agree; both, when they do not,
 * because a mismatch right after a deploy is the thing worth noticing.
 */
export function versionLine(web: string, api: string | undefined): string {
  if (api === undefined) return `v${web} · API unreachable`;
  return api === web ? `v${web}` : `v${web} · API v${api}`;
}

export function AppShell() {
  const { session, me } = useSessionContext();
  const roles = useRoles();
  const location = useLocation();
  const who = session?.user.email ?? session?.user.id ?? '';
  const health = useLoad((signal) => fetchHealth(signal));
  const version = versionLine(appVersion(), health.load.state === 'ok' ? health.load.data.version : health.load.state === 'error' ? undefined : appVersion());

  // The local copy paints first; the person's saved preference, once read, wins (D-084).
  const [collapsed, setCollapsed] = useState<boolean>(() => readStoredFlag(browserStorage(), NAV_COLLAPSED_STORAGE_KEY) ?? false);
  const pref = useLoad((signal) => fetchNavCollapsedPreference(signal));
  const prefValue = pref.load.state === 'ok' ? pref.load.data.value : undefined;
  useEffect(() => {
    if (prefValue === undefined) return;
    const next = resolveFlag(readStoredFlag(browserStorage(), NAV_COLLAPSED_STORAGE_KEY), prefValue, false);
    setCollapsed(next);
    storeFlag(browserStorage(), NAV_COLLAPSED_STORAGE_KEY, next);
  }, [prefValue]);
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    storeFlag(browserStorage(), NAV_COLLAPSED_STORAGE_KEY, next);
    void putNavCollapsedPreference(next).catch(() => undefined); // the local copy still holds it; the next toggle tries again
  };

  // The drawer (D-215): open only on a narrow screen, shut by any move to another address.
  const narrow = useNarrow();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawer = narrow && drawerOpen;
  useEffect(() => setDrawerOpen(false), [location.pathname, location.search, narrow]);
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer]);
  // A narrow screen's tab rows scroll sideways; the open tab is kept in view (lib/tabsInView.ts).
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!narrow || !mainRef.current) return;
    return keepActiveTabsInView(mainRef.current);
  }, [narrow]);

  const folded = railFolded(collapsed, narrow);
  const sections = folded ? foldSubItems(navFor(roles)) : navFor(roles);
  const roleText = me.state === 'ok' ? (roles.length > 0 ? roles.join(' · ') : 'No roles yet') : me.state === 'loading' ? 'Loading…' : 'Could not load roles';
  const toggleLabel = collapsed ? 'Expand the menu' : 'Collapse the menu';
  const drawerLabel = drawer ? 'Close the menu' : 'Open the menu';

  return (
    <div className={`shell${folded ? ' shell--collapsed' : ''}${drawer ? ' shell--drawer' : ''}`}>
      <header className="topbar">
        <button type="button" className="topbar__menu" onClick={() => setDrawerOpen(!drawerOpen)} title={drawerLabel} aria-label={drawerLabel} aria-expanded={drawer} aria-controls="app-rail">
          <Icon name={drawer ? 'close' : 'menu'} />
        </button>
        <NavLink to="/" className="brand topbar__brand" aria-label="UnitedOS home">
          <img className="brand__mark" src="/favicon.png" alt="" width="32" height="32" />
          <span className="brand__text">
            <span className="brand__name">UnitedOS</span>
            <span className="brand__tag">United Operating System</span>
          </span>
        </NavLink>
        <div className="topbar__search">
          <SearchBox />
        </div>
        <div className="topbar__actions">
          {canSee(SETTINGS_LINK, roles) && (
            <NavLink to={SETTINGS_LINK.to} className={({ isActive }) => `topbar__icon${isActive ? ' topbar__icon--active' : ''}`} aria-label={SETTINGS_LINK.label} title={SETTINGS_LINK.label}>
              <Icon name="settings" />
            </NavLink>
          )}
          <AccountMenu who={who} roles={roles} roleText={roleText} roleError={me.state === 'error'} version={version} />
        </div>
      </header>
      <aside className="shell__sidebar" id="app-rail">
        <div className="sidebar__top">
          <button type="button" className="sidebar__toggle" onClick={toggle} title={toggleLabel} aria-label={toggleLabel} aria-expanded={!collapsed} aria-controls="app-rail">
            <Icon name={collapsed ? 'chevrons-right' : 'chevrons-left'} className="nav__icon" />
          </button>
        </div>
        <nav className="nav" aria-label="Main">
          {sections.map((section, i) => (
            <div className="nav__section" key={section.title ?? i}>
              {section.title && <div className="nav__title">{section.title}</div>}
              {section.items.map((item) => {
                // A place not built yet: the label and its mark, nothing to click (no route exists for it).
                if (item.soon)
                  return (
                    <span key={item.to} className="nav__link nav__link--soon" title={folded ? `${item.label} — coming soon` : undefined} aria-disabled="true">
                      {item.icon && <Icon name={item.icon} className="nav__icon" />}
                      <span className="nav__label">
                        {item.label}
                        <sup className="nav__soon">Coming soon</sup>
                      </span>
                    </span>
                  );
                const foldedActive = folded && (item.alsoActive ?? []).some((p) => location.pathname.startsWith(p));
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end ?? false}
                    title={folded ? item.label : undefined}
                    className={({ isActive }) => `nav__link${item.sub ? ' nav__link--sub' : ''}${isActive || foldedActive ? ' nav__link--active' : ''}`}
                  >
                    {item.icon && <Icon name={item.icon} className="nav__icon" />}
                    <span className="nav__label">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
      {drawer && <div className="shell__scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      {/* The Home opening plays over the whole window, once per tab, on the first arrival at Home (components/HomeOpening.tsx). */}
      <HomeOpening />
      {/* The Manager Meeting entrance plays over the page's area only, beside <main> (components/MeetingEntrance.tsx). */}
      <MeetingEntranceProvider>
        <main className="shell__main" ref={mainRef}>
          {/* A page's chunk loading on its first visit (App.tsx, contexts/25) shows here, inside the shell — never in place of it; so does a page that threw, until the next address. */}
          <PageBoundary resetKey={location.pathname}>
            <Outlet />
          </PageBoundary>
        </main>
      </MeetingEntranceProvider>
    </div>
  );
}
